import { FormControl } from "@angular/forms";
import { Links } from 'app/shared/model/link.model';
import { Observable } from 'rxjs';

/**
 *  https://en.wikipedia.org/wiki/Hypertext_Application_Language
 */
export interface HALResponse<ResponseType> {
	_embedded: ResponseType;
	_links: Links;
	page: HALPageDetails;
}

export interface HALPageDetails {
	size: number;
	totalElements: number;
	totalPages: number;
	number: number;
}

export type Nullable<T> = T | null;

export type Optional<T> = T | undefined;

export type Nullish<T> = T | null | undefined;

export module Optional {
	export function of<T>(defaultVal?: T): Optional<T> {
		return defaultVal;
	}
	
	export function isNotUndefined<T>(val: T): val is NonNullable<T> {
		return val != null;
	}
}

export module Nullable {
	export function of<T>(defaultVal: T | null = null): Nullable<T> {
		return defaultVal;
	}
	
	export function isNotNull<T>(val: T): val is NonNullable<T> {
		return val != null;
	}
}

export module Nullish {
	export function of<T>(defaultVal: T | null | undefined = undefined): Nullish<T> {
		return defaultVal;
	}
	
	export function isNotNullish<T>(val: T): val is NonNullable<T> {
		return val != null;
	}
}

export type ObservableSource<T> = {
	[K in keyof T]: Observable<T[K]>
}

export namespace JSON {
	export type Object = { [key: string]: Value };
	export type Array = Value[];
	export type Value = string | number | boolean | null | Array | Object;
}

export type KeyOf<T, K extends keyof T> = keyof Pick<T, K>;

export type OmitKeyOf<T, K extends keyof T> = keyof Omit<T, K>;

export type PartialKey<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type PartialExcept<T, K extends keyof T> = Partial<Omit<T, K>> & Pick<T, K>;

export type RequiredKey<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export interface TypedGetterSetter<T, V> extends Pick<PropertyDescriptor, 'get' | 'set' | 'enumerable'> {
	get: (this: T) => V
	set: (this: T, val: V) => void
}

export type TypedGetter<T, V> = Omit<TypedGetterSetter<T, V>, 'set'>;

export type TypedGetterMap = {
	[key: string]: TypedGetter<any, any>
}

export type TypedGetterSetterMap = {
	[key: string]: TypedGetterSetter<any, any>
}

export type ArrayGeneric<T extends any[]> = T extends Array<infer U> ? U : never;

type KeyMapU<T, K extends keyof T> = Map<K, T[K]>;
export type KeyMap<T> = KeyMapU<T, keyof T>;


export type ObservableValueMap<T> = {
	[K in keyof T]: T[K] extends Observable<infer U> ? U : never
}

export type BasicRecord<T = unknown> = Record<PropertyKey, T>;

export type Vararg<T> = T | T[];

type ValueType = boolean | number | string | Date;

/**
 * A Type to represent a FormControl that conforms to when using **new FormControl()**.
 *
 * @example
 * new FormControl<string>()
 */
export type RealFormControl<T> = FormControl<T | null>;

/**
 * Type to represent a map of FormControls that conforms to when using **new FormControl()** per key.
 * Can be used to represent a nested object/array of FormControls. Takes **EType** as a generic to specify the available types of the FormControl.
 */
export type FormControlMap<T extends object, EType = ValueType> = {
	[K in keyof T]: NonNullable<T[K]> extends EType
		? RealFormControl<T[K]>
		: T[K] extends Array<infer AType>
			? NonNullable<AType> extends EType
				? Array<RealFormControl<AType>>
				: AType extends object
					? Array<FormControlMap<AType, EType>>
					: unknown
			: T[K] extends object
				? FormControlMap<T[K], EType>
				: unknown;
}